import type { ElevationPoint } from '../types';

/**
 * Configuration parameters for climb detection
 */
export interface ClimbDetectionConfig {
  /** Minimum elevation gain (in meters) to be considered a climb */
  minElevationGain: number;
  /** Minimum distance (in meters) to be considered a climb */
  minDistance: number;
  /** Minimum average gradient (in %) to be considered a climb */
  minAverageGradient: number;
  /** Maximum allowed descent (in meters) withi      climbs.push(climb);
    } else {
      // Note: Removed sub-climb detection to prevent nested climbs
    }
  } else {
  }re it's considered broken */
  maxDescentInClimb: number;
  /** Smoothing window size for gradient calculation */
  smoothingWindow: number;
  /** Minimum climb length for categorization (in meters) */
  categorizationMinLength: number;
  /** Maximum gap (in meters) between climbs to consider merging them */
  maxMergeGap: number;
  /** Maximum descent (in meters) between climbs to allow merging */
  maxMergeDescentBetween: number;
  /** Minimum segment length (in meters) for max gradient calculation */
  minSegmentLengthForGradient: number;
  /** Maximum realistic gradient (in %) to cap unrealistic values */
  maxRealisticGradient: number;
  /** Window size (in meters) for max gradient calculation */
  gradientCalculationWindow: number;
}

/**
 * Default climb detection configuration (based on common cycling standards)
 */
export const DEFAULT_CLIMB_CONFIG: ClimbDetectionConfig = {
  minElevationGain: 50,        // 50m minimum elevation gain
  minDistance: 300,            // 300m minimum distance
  minAverageGradient: 2,       // 2% minimum average gradient (reduced from 3%)
  maxDescentInClimb: 15,       // Allow max 15m descent within a climb (reduced from 80m)
  smoothingWindow: 5,          // Smooth over 5 points
  categorizationMinLength: 500, // 500m minimum for categorization
  maxMergeGap: 2000,          // Max 2km gap between climbs to merge (increased significantly)
  maxMergeDescentBetween: 50,  // Max 50m descent between climbs to allow merging (increased significantly)
  minSegmentLengthForGradient: 50, // Min 50m segment for gradient calculation (increased)
  maxRealisticGradient: 15,    // Cap gradients at 15% (more realistic maximum)
  gradientCalculationWindow: 100 // Use 100m windows for max gradient calculation (increased)
};

/**
 * Climb category classification (similar to Tour de France categories)
 */
export type ClimbCategory = 'Cat 4' | 'Cat 3' | 'Cat 2' | 'Cat 1' | 'HC';

export const ClimbCategories = {
  CATEGORY_4: 'Cat 4' as const,
  CATEGORY_3: 'Cat 3' as const,
  CATEGORY_2: 'Cat 2' as const,
  CATEGORY_1: 'Cat 1' as const,
  HORS_CATEGORY: 'HC' as const
} as const;

/**
 * Detected climb segment
 */
export interface ClimbSegment {
  /** Unique identifier for the climb */
  id: string;
  /** Start distance along route (in km) */
  startDistance: number;
  /** End distance along route (in km) */
  endDistance: number;
  /** Start elevation (in meters) */
  startElevation: number;
  /** End elevation (in meters) */
  endElevation: number;
  /** Total elevation gain (in meters) */
  elevationGain: number;
  /** Length of climb (in meters) */
  length: number;
  /** Average gradient (in %) */
  averageGradient: number;
  /** Climb category classification */
  category: ClimbCategory | null;
  /** Difficulty points (used for categorization) */
  difficultyPoints: number;
  /** Start index in elevation data array */
  startIndex: number;
  /** End index in elevation data array */
  endIndex: number;
  /** Elevation profile points for this climb */
  elevationProfile: ElevationPoint[];
}

function smoothElevationData(
  elevationData: ElevationPoint[], 
  windowSize: number
): ElevationPoint[] {
  if (windowSize <= 1) return elevationData;
  
  const smoothed: ElevationPoint[] = [];
  const halfWindow = Math.floor(windowSize / 2);
  
  for (let i = 0; i < elevationData.length; i++) {
    const start = Math.max(0, i - halfWindow);
    const end = Math.min(elevationData.length - 1, i + halfWindow);
    
    let sumElevation = 0;
    let count = 0;
    
    for (let j = start; j <= end; j++) {
      sumElevation += elevationData[j].elevation;
      count++;
    }
    
    smoothed.push({
      ...elevationData[i],
      elevation: sumElevation / count
    });
  }
  
  return smoothed;
}

/**
 * Calculate difficulty points for climb categorization
 * Based on a combination of elevation gain and average gradient
 */
function calculateDifficultyPoints(
  elevationGain: number, 
  length: number, 
  averageGradient: number
): number {
  // Base points from elevation gain
  let points = elevationGain;
  
  // Bonus points for steep gradients (more conservative for moderate gradients)
  if (averageGradient > 10) {
    points *= 1.8; // Extreme gradients get significant bonus
  } else if (averageGradient > 8) {
    points *= 1.5; // Very steep
  } else if (averageGradient > 6) {
    points *= 1.3; // Steep
  } else if (averageGradient > 5) {
    points *= 1.15; // Moderately steep
  } else if (averageGradient > 4) {
    points *= 1.05; // Slightly steep (reduced from 1.1)
  }
  // Gradients ≤4% get no bonus (they're more about endurance than difficulty)
  
  // More conservative length bonuses - long gradual climbs shouldn't be overpunished
  if (length > 20000) { // > 20km - very long climbs
    points *= 1.4; // Reduced from 1.5
  } else if (length > 15000) { // > 15km - long climbs
    points *= 1.25; // New tier for better granularity
  } else if (length > 10000) { // > 10km - moderately long
    points *= 1.15; // Reduced from 1.5
  } else if (length > 5000) { // > 5km
    points *= 1.1; // Reduced from 1.3
  } else if (length > 2000) { // > 2km
    points *= 1.05; // Reduced from 1.2
  } else if (length > 1000) { // > 1km
    points *= 1.02; // Much reduced from 1.25
  }
  
  return Math.round(points);
}

/**
 * Classify climb based on difficulty points
 */
function classifyClimb(
  difficultyPoints: number, 
  length: number, 
  config: ClimbDetectionConfig
): ClimbCategory | null {
  // Only categorize climbs that meet minimum length requirement
  if (length < config.categorizationMinLength) {
    return null;
  }
  
  // More realistic thresholds based on actual cycling standards
  if (difficultyPoints >= 1200) return ClimbCategories.HORS_CATEGORY; // Increased from 800 - truly exceptional climbs
  if (difficultyPoints >= 600) return ClimbCategories.CATEGORY_1;     // Increased from 400 - major climbs
  if (difficultyPoints >= 300) return ClimbCategories.CATEGORY_2;     // Increased from 200 - significant climbs
  if (difficultyPoints >= 150) return ClimbCategories.CATEGORY_3;     // Increased from 100 - moderate climbs
  if (difficultyPoints >= 45) return ClimbCategories.CATEGORY_4;      // Kept at 45 - minor climbs
  
  return null;
}

/**
 * Detect climb segments from elevation data
 */
export function detectClimbs(
  elevationData: ElevationPoint[], 
  config: ClimbDetectionConfig = DEFAULT_CLIMB_CONFIG
): ClimbSegment[] {
  
  if (elevationData.length < 2) {
    return [];
  }

  // Smooth the elevation data to reduce noise
  const smoothedData = smoothElevationData(elevationData, config.smoothingWindow);
  
  const climbs: ClimbSegment[] = [];
  let climbStart: number | null = null;
  let climbStartElevation = 0;
  
  for (let i = 1; i < smoothedData.length; i++) {
    const current = smoothedData[i];
    const previous = smoothedData[i - 1];
    const elevationChange = current.elevation - previous.elevation;
    
    // Starting a potential climb - require minimum gradient to start
    if (climbStart === null && elevationChange > 0) {
      // Calculate gradient over a reasonable window (e.g., 200m ahead) to avoid starting on tiny bumps
      const startWindowDistance = 200; // meters
      let endWindowIndex = i;
      for (let j = i + 1; j < smoothedData.length; j++) {
        const distAhead = (smoothedData[j].distance - current.distance) * 1000;
        if (distAhead >= startWindowDistance) {
          endWindowIndex = j;
          break;
        }
      }
      
      const windowDistance = (smoothedData[endWindowIndex].distance - previous.distance) * 1000;
      const windowElevGain = smoothedData[endWindowIndex].elevation - previous.elevation;
      const windowGradient = windowDistance > 0 ? (windowElevGain / windowDistance) * 100 : 0;
      
      // Only start a climb if the gradient over the next 200m is at least 2%
      if (windowGradient >= config.minAverageGradient) {
        climbStart = i - 1;
        climbStartElevation = previous.elevation;
      }
    }
    
    // DISABLED: Restart logic was creating overlapping climbs and ending climbs too early
    // The climb should just continue naturally to its peak
    /*
    // Check if we should restart the climb when gradient becomes significantly steeper
    else if (climbStart !== null && elevationChange > 0) {
      // Check if we've been climbing for a while with low gradient
      const currentDistance = (current.distance - smoothedData[climbStart].distance) * 1000; // meters
      if (currentDistance > 1000) { // Only check after 1km of climbing
        const currentElevationGain = current.elevation - climbStartElevation;
        const currentAvgGradient = (currentElevationGain / currentDistance) * 100;
        
        // Calculate recent gradient over last 500m
        let recentStartIndex = i;
        for (let j = i - 1; j >= 0; j--) {
          const dist = (current.distance - smoothedData[j].distance) * 1000;
          if (dist >= 500) {
            recentStartIndex = j;
            break;
          }
        }
        const recentDistance = (current.distance - smoothedData[recentStartIndex].distance) * 1000;
        const recentElevGain = current.elevation - smoothedData[recentStartIndex].elevation;
        const recentGradient = (recentElevGain / recentDistance) * 100;
        
        // If recent gradient is much steeper than average, restart the climb here
        if (recentGradient > currentAvgGradient + 3 && recentGradient >= 5) {
          
          // End the previous gradual climb section
          const previousPeakIndex = i - 1;
          analyzeAndAddClimb(
            smoothedData,
            elevationData,
            climbStart,
            previousPeakIndex,
            climbStartElevation,
            config,
            climbs
          );
          
          // Start new climb at the steeper section
          climbStart = recentStartIndex;
          climbStartElevation = smoothedData[recentStartIndex].elevation;
        }
      }
    }
    */
    // Check if we should end a climb
    else if (climbStart !== null) {
      // Check if the climb has become too gradual
      // Calculate recent gradient over last 500m to see if we should end the climb
      const recentWindowDistance = 500; // meters
      let recentStartIndex = i;
      for (let j = i - 1; j >= climbStart; j--) {
        const distBack = (current.distance - smoothedData[j].distance) * 1000;
        if (distBack >= recentWindowDistance) {
          recentStartIndex = j;
          break;
        }
      }
      
      const recentDistance = (current.distance - smoothedData[recentStartIndex].distance) * 1000;
      const recentElevGain = current.elevation - smoothedData[recentStartIndex].elevation;
      const recentGradient = recentDistance > 0 ? (recentElevGain / recentDistance) * 100 : 0;
      
      // If the recent gradient is below minimum AND we've been climbing for at least 1km,
      // end this climb and potentially start a new one later when it gets steep again
      const climbDistance = (current.distance - smoothedData[climbStart].distance) * 1000;
      if (climbDistance > 1000 && recentGradient < config.minAverageGradient && recentElevGain > 0) {
        // End the previous climb section
        const previousPeakIndex = i - 1;
        analyzeAndAddClimb(
          smoothedData,
          elevationData,
          climbStart,
          previousPeakIndex,
          climbStartElevation,
          config,
          climbs
        );
        
        // Reset climb tracking
        climbStart = null;
        climbStartElevation = 0;
        continue;
      }
      
      // Look ahead to see if this is really the end of the climb
      const shouldEndClimb = shouldEndClimbHere(smoothedData, i, climbStart, climbStartElevation, config);
      
      if (shouldEndClimb) {
        // Find the highest point in the climb to end there (not at the descent)
        let peakIndex = climbStart;
        let peakElevation = smoothedData[climbStart].elevation;
        for (let j = climbStart; j < i; j++) {
          if (smoothedData[j].elevation > peakElevation) {
            peakElevation = smoothedData[j].elevation;
            peakIndex = j;
          }
        }
        
        // Analyze the climb we just finished (ending at peak, not at descent)
        analyzeAndAddClimb(
          smoothedData,
          elevationData,
          climbStart,
          peakIndex,
          climbStartElevation,
          config,
          climbs
        );
        
        // Reset for next potential climb
        climbStart = null;
      }
    }
  }
  
  // Handle climb that goes to the end of the route
  if (climbStart !== null) {
    // Find the highest point in the climb to end there
    let peakIndex = climbStart;
    let peakElevation = smoothedData[climbStart].elevation;
    for (let j = climbStart; j < smoothedData.length; j++) {
      if (smoothedData[j].elevation > peakElevation) {
        peakElevation = smoothedData[j].elevation;
        peakIndex = j;
      }
    }
    
    analyzeAndAddClimb(
      smoothedData,
      elevationData,
      climbStart,
      peakIndex,
      climbStartElevation,
      config,
      climbs
    );
  }
  
  // Remove any overlapping climbs first
  const nonOverlappingClimbs = removeOverlappingClimbs(climbs);
  
  // Merge nearby climbs that should be considered continuous
  const mergedClimbs = mergeNearbyClimbs(nonOverlappingClimbs, elevationData, config);
  
  // Sort climbs by their starting distance along the route
  mergedClimbs.sort((a, b) => a.startDistance - b.startDistance);
  
  // Reassign sequential IDs to final climbs (after filtering and merging)
  const finalClimbs = mergedClimbs.map((climb, index) => ({
    ...climb,
    id: `climb-${index + 1}`
  }));
  
  return finalClimbs;
}

/**
 * Intelligently determine if a climb should end at the current position
 */
function shouldEndClimbHere(
  smoothedData: ElevationPoint[],
  currentIndex: number,
  climbStart: number,
  climbStartElevation: number,
  config: ClimbDetectionConfig
): boolean {
  const current = smoothedData[currentIndex];
  
  // Calculate current elevation gain from start of climb
  const currentElevationGain = current.elevation - climbStartElevation;
  
  // Don't end climb if we haven't gained enough elevation yet
  if (currentElevationGain < config.minElevationGain) {
    return false;
  }
  
  // Look ahead to see if climbing resumes
  const lookAheadDistance = 2500; // Look ahead 2.5km (increased for longer climbs)
  let maxElevationAhead = current.elevation;
  let significantClimbingResumes = false;
  let totalElevationGainAhead = 0;
  
  for (let j = currentIndex + 1; j < smoothedData.length; j++) {
    const ahead = smoothedData[j];
    const distanceAhead = (ahead.distance - current.distance) * 1000; // Convert to meters
    
    if (distanceAhead > lookAheadDistance) break;
    
    if (ahead.elevation > maxElevationAhead) {
      maxElevationAhead = ahead.elevation;
    }
    
    // Calculate cumulative elevation gain ahead
    if (j > currentIndex + 1) {
      const elevChange = ahead.elevation - smoothedData[j - 1].elevation;
      if (elevChange > 0) {
        totalElevationGainAhead += elevChange;
      }
    }
  }
  
  // Check if there's significant climbing ahead:
  // Either a single point much higher OR continuous climbing (total gain > 20m)
  const hasSingleHighPoint = maxElevationAhead > current.elevation + 10;
  const hasContinuousClimbing = totalElevationGainAhead > 20;
  significantClimbingResumes = hasSingleHighPoint || hasContinuousClimbing;
  
  // Calculate the descent from the peak so far
  const highestSoFar = Math.max(...smoothedData.slice(climbStart, currentIndex + 1).map(p => p.elevation));
  const descentFromPeak = highestSoFar - current.elevation;
  
  // Check if the climbing continues to a higher point than our current peak
  const climbsContinuesHigher = maxElevationAhead > highestSoFar;
  
  // Check if we're past the peak and descending with no climb ahead
  const noClimbAhead = !significantClimbingResumes && !climbsContinuesHigher;
  
  // Check if we're AT or just past the peak
  const atOrNearPeak = descentFromPeak <= 2; // Within 2m of peak
  const definitelyPastPeak = descentFromPeak > 2;
  
  // Only end climb if:
  // 1. (Significant descent from peak AND no climbing ahead) OR
  // 2. (At/near peak and no climbing ahead - end immediately at peak!) OR
  // 3. (Past peak and definitely no climbing ahead)
  const hasSignificantDescent = descentFromPeak > config.maxDescentInClimb;
  const endAtPeak = atOrNearPeak && noClimbAhead && totalElevationGainAhead < 2;
  const endAfterPeak = definitelyPastPeak && noClimbAhead && totalElevationGainAhead < 5;
  const shouldEnd = (hasSignificantDescent && noClimbAhead) || endAtPeak || endAfterPeak;
  
  return shouldEnd;
}

/**
 * Find steeper sub-climbs within a long gradual climb
 */
function findSubClimbs(
  smoothedData: ElevationPoint[],
  originalData: ElevationPoint[],
  startIndex: number,
  endIndex: number,
  config: ClimbDetectionConfig,
  climbs: ClimbSegment[]
): void {
  
  // Use a sliding window approach to find steeper sections
  const minSubClimbPoints = Math.ceil(config.minDistance / 50); // Approximate points for minimum distance
  const maxSubClimbPoints = Math.ceil(5000 / 50); // Maximum 5km sub-climbs
  
  for (let windowSize = minSubClimbPoints; windowSize <= maxSubClimbPoints; windowSize += 10) {
    for (let i = startIndex; i <= endIndex - windowSize; i++) {
      const subStart = i;
      const subEnd = Math.min(i + windowSize, endIndex);
      
      const subStartElevation = smoothedData[subStart].elevation;
      const subEndElevation = smoothedData[subEnd].elevation;
      const subElevationGain = subEndElevation - subStartElevation;
      
      if (subElevationGain <= 0) continue; // Skip descents or flat sections
      
      const subStartDistance = smoothedData[subStart].distance * 1000;
      const subEndDistance = smoothedData[subEnd].distance * 1000;
      const subLength = subEndDistance - subStartDistance;
      
      // Check if this sub-segment meets criteria
      if (subElevationGain >= config.minElevationGain && subLength >= config.minDistance) {
        const subGradient = (subElevationGain / subLength) * 100;
        
        if (subGradient >= config.minAverageGradient) {
          
          // Check if this overlaps with existing climbs
          const overlaps = climbs.some(existingClimb => 
            (subStart >= existingClimb.startIndex && subStart <= existingClimb.endIndex) ||
            (subEnd >= existingClimb.startIndex && subEnd <= existingClimb.endIndex) ||
            (subStart <= existingClimb.startIndex && subEnd >= existingClimb.endIndex)
          );
          
          if (!overlaps) {
            const difficultyPoints = calculateDifficultyPoints(subElevationGain, subLength, subGradient);
            const category = classifyClimb(difficultyPoints, subLength, config);
            
            const elevationProfile = originalData.slice(subStart, subEnd + 1);
            
            const climb: ClimbSegment = {
              id: `climb-${climbs.length + 1}`,
              startDistance: smoothedData[subStart].distance,
              endDistance: smoothedData[subEnd].distance,
              startElevation: subStartElevation,
              endElevation: subEndElevation,
              elevationGain: subElevationGain,
              length: subLength,
              averageGradient: subGradient,
              category,
              difficultyPoints,
              startIndex: subStart,
              endIndex: subEnd,
              elevationProfile
            };
            
            climbs.push(climb);
          }
        }
      }
    }
  }
}

/**
 * Analyze a potential climb and add it to the climbs array if it meets criteria
 */
function analyzeAndAddClimb(
  smoothedData: ElevationPoint[],
  originalData: ElevationPoint[],
  startIndex: number,
  endIndex: number,
  startElevation: number,
  config: ClimbDetectionConfig,
  climbs: ClimbSegment[]
): void {
  const endElevation = smoothedData[endIndex].elevation;
  const elevationGain = endElevation - startElevation;
  const startDistance = smoothedData[startIndex].distance * 1000; // Convert to meters
  const endDistance = smoothedData[endIndex].distance * 1000; // Convert to meters
  const length = endDistance - startDistance;
  
  // Check if climb meets minimum criteria
  if (
    elevationGain >= config.minElevationGain &&
    length >= config.minDistance
  ) {
    const averageGradient = (elevationGain / length) * 100;
    
    if (averageGradient >= config.minAverageGradient) {
      const difficultyPoints = calculateDifficultyPoints(elevationGain, length, averageGradient);
      const category = classifyClimb(difficultyPoints, length, config);
      
      // Extract elevation profile for this climb from original data
      const elevationProfile = originalData.slice(startIndex, endIndex + 1);
      
      const climb: ClimbSegment = {
        id: `climb-${climbs.length + 1}`,
        startDistance: smoothedData[startIndex].distance,
        endDistance: smoothedData[endIndex].distance,
        startElevation,
        endElevation,
        elevationGain,
        length,
        averageGradient,
        category,
        difficultyPoints,
        startIndex,
        endIndex,
        elevationProfile
      };
      
      climbs.push(climb);
    } else {
      
      // If the overall climb doesn't meet gradient criteria but is long,
      // try to find steeper sub-segments within it
      if (length > 5000) { // Only for climbs longer than 5km
        findSubClimbs(smoothedData, originalData, startIndex, endIndex, config, climbs);
      }
    }
  } else {
  }
}

/**
 * Format climb information for display
 */
export function formatClimbInfo(climb: ClimbSegment): {
  title: string;
  distance: string;
  elevationGain: string;
  averageGradient: string;
  category: string;
  length: string;
} {
  return {
    title: `Climb ${climb.id.split('-')[1]}`,
    distance: `${climb.startDistance.toFixed(1)} - ${climb.endDistance.toFixed(1)} km`,
    elevationGain: `${Math.round(climb.elevationGain)}m`,
    averageGradient: `${climb.averageGradient.toFixed(1)}%`,
    category: climb.category || 'Uncat.',
    length: `${(climb.length / 1000).toFixed(1)} km`
  };
}

/**
 * Get climb category color for UI styling
 */
export function getClimbCategoryColor(category: ClimbCategory | null): string {
  switch (category) {
    case ClimbCategories.HORS_CATEGORY:
      return '#8B0000'; // Dark red
    case ClimbCategories.CATEGORY_1:
      return '#FF0000'; // Red
    case ClimbCategories.CATEGORY_2:
      return '#FF8C00'; // Dark orange
    case ClimbCategories.CATEGORY_3:
      return '#FFD700'; // Gold
    case ClimbCategories.CATEGORY_4:
      return '#32CD32'; // Lime green
    default:
      return '#808080'; // Gray for uncategorized
  }
}

/**
 * Get the total climbing statistics for the route
 */
export function getClimbingStats(climbs: ClimbSegment[]): {
  totalClimbs: number;
  totalElevationGain: number;
  totalClimbingDistance: number;
  categorizedClimbs: Record<ClimbCategory, number>;
  averageGradient: number;
} {
  const categorizedClimbs = {
    [ClimbCategories.HORS_CATEGORY]: 0,
    [ClimbCategories.CATEGORY_1]: 0,
    [ClimbCategories.CATEGORY_2]: 0,
    [ClimbCategories.CATEGORY_3]: 0,
    [ClimbCategories.CATEGORY_4]: 0
  };
  
  let totalElevationGain = 0;
  let totalClimbingDistance = 0;
  let totalWeightedGradient = 0;
  
  climbs.forEach(climb => {
    totalElevationGain += climb.elevationGain;
    totalClimbingDistance += climb.length;
    totalWeightedGradient += climb.averageGradient * climb.length;
    
    if (climb.category) {
      categorizedClimbs[climb.category]++;
    }
  });
  
  const averageGradient = totalClimbingDistance > 0 ? totalWeightedGradient / totalClimbingDistance : 0;
  
  return {
    totalClimbs: climbs.length,
    totalElevationGain: Math.round(totalElevationGain),
    totalClimbingDistance: Math.round(totalClimbingDistance),
    categorizedClimbs,
    averageGradient: Math.round(averageGradient * 10) / 10 // Round to 1 decimal
  };
}

/**
 * Remove overlapping climbs, keeping the longer/higher difficulty one
 */
function removeOverlappingClimbs(climbs: ClimbSegment[]): ClimbSegment[] {
  if (climbs.length <= 1) return climbs;
  
  // Sort climbs by start distance to make overlap detection easier
  const sortedClimbs = [...climbs].sort((a, b) => a.startDistance - b.startDistance);
  const nonOverlapping: ClimbSegment[] = [];
  
  for (const climb of sortedClimbs) {
    // Check if this climb overlaps with any previously added climb
    const overlapsWithExisting = nonOverlapping.some(existing => {
      const hasOverlap = (
        (climb.startDistance >= existing.startDistance && climb.startDistance <= existing.endDistance) ||
        (climb.endDistance >= existing.startDistance && climb.endDistance <= existing.endDistance) ||
        (climb.startDistance <= existing.startDistance && climb.endDistance >= existing.endDistance)
      );
      
      if (hasOverlap) {
        // Keep the climb with higher difficulty points (better climb)
        if (climb.difficultyPoints > existing.difficultyPoints) {
          const existingIndex = nonOverlapping.indexOf(existing);
          nonOverlapping.splice(existingIndex, 1);
          return false; // Allow this climb to be added
        } else {
          return true; // Skip this climb
        }
      }
      return false;
    });
    
    if (!overlapsWithExisting) {
      nonOverlapping.push(climb);
    }
  }
  
  return nonOverlapping;
}

/**
 * Merge nearby climb segments that should be considered as one continuous climb
 */
function mergeNearbyClimbs(
  climbs: ClimbSegment[],
  elevationData: ElevationPoint[],
  config: ClimbDetectionConfig
): ClimbSegment[] {
  if (climbs.length <= 1) return climbs;
  
  const mergedClimbs: ClimbSegment[] = [];
  let currentClimb = climbs[0];
  
  for (let i = 1; i < climbs.length; i++) {
    const nextClimb = climbs[i];
    
    // Calculate gap between climbs
    const gapDistance = (nextClimb.startDistance - currentClimb.endDistance) * 1000; // Convert to meters
    
    // Get elevation at the end of current climb and start of next climb
    const currentEndElevation = elevationData[currentClimb.endIndex]?.elevation || currentClimb.endElevation;
    const nextStartElevation = elevationData[nextClimb.startIndex]?.elevation || nextClimb.startElevation;
    const elevationDrop = currentEndElevation - nextStartElevation;
    
    // Check if we should merge these climbs
    const shouldMerge = (
      gapDistance <= config.maxMergeGap &&
      elevationDrop <= config.maxMergeDescentBetween
    );
    
    if (shouldMerge) {
      // Merge the climbs
      const mergedStartDistance = currentClimb.startDistance;
      const mergedEndDistance = nextClimb.endDistance;
      const mergedStartElevation = currentClimb.startElevation;
      const mergedEndElevation = nextClimb.endElevation;
      const mergedElevationGain = mergedEndElevation - mergedStartElevation;
      const mergedLength = (mergedEndDistance - mergedStartDistance) * 1000; // Convert to meters
      const mergedAverageGradient = (mergedElevationGain / mergedLength) * 100;
      
      const mergedDifficultyPoints = calculateDifficultyPoints(
        mergedElevationGain, 
        mergedLength, 
        mergedAverageGradient
      );
      const mergedCategory = classifyClimb(mergedDifficultyPoints, mergedLength, config);
      
      // Create merged climb
      currentClimb = {
        id: `climb-${mergedClimbs.length + 1}`,
        startDistance: mergedStartDistance,
        endDistance: mergedEndDistance,
        startElevation: mergedStartElevation,
        endElevation: mergedEndElevation,
        elevationGain: mergedElevationGain,
        length: mergedLength,
        averageGradient: mergedAverageGradient,
        category: mergedCategory,
        difficultyPoints: mergedDifficultyPoints,
        startIndex: currentClimb.startIndex,
        endIndex: nextClimb.endIndex,
        elevationProfile: elevationData.slice(currentClimb.startIndex, nextClimb.endIndex + 1)
      };
    } else {
      // No merge - add current climb and move to next
      mergedClimbs.push(currentClimb);
      currentClimb = nextClimb;
    }
  }
  
  // Add the final climb
  mergedClimbs.push(currentClimb);
  
  // Ensure climbs are sorted by starting distance
  mergedClimbs.sort((a, b) => a.startDistance - b.startDistance);
  
  return mergedClimbs;
}
